import { Link } from 'react-router-dom';
import { Award, Heart, Home, Users, Calendar } from 'lucide-react';
import Layout from '@/components/Layout';

export default function HomePage() {
  return (
    <Layout>
      {/* Banner */}
      <section className="banner-container py-2 bg-white">
        <img 
          src="/images/Banner.png" 
          alt="BN Physiotherapy Banner" 
          className="banner-image max-h-[400px]"
          data-testid="hero-banner"
        />
      </section>

      {/* Our Mission & Meet Bong Section - Side by Side */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Our Mission - Turquoise Background */}
            <div className="bg-[#5ce1e6] p-8 lg:p-12 rounded-l-2xl lg:rounded-l-2xl rounded-t-2xl lg:rounded-t-none">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Our Mission
              </h2>
              <p className="text-slate-800 leading-relaxed text-lg mb-4">
                To provide compassionate, evidence-based physiotherapy care that empowers patients to achieve their optimal physical function and quality of life. Through specialized expertise in neurological rehabilitation and a patient-centered approach, Bong is committed to supporting each individual's unique recovery journey.
              </p>
              <p className="text-slate-800 leading-relaxed text-lg mb-6">
                For patients unable to travel, we offer home physiotherapy visits across Johannesburg's northern and western suburbs.
              </p>
              <Link 
                to="/book" 
                className="inline-flex items-center gap-3 px-8 py-4 bg-slate-900 text-white rounded-full font-semibold text-lg hover:bg-slate-800 transition-colors shadow-lg"
                data-testid="home-book-appointment-btn"
              >
                <Calendar className="w-6 h-6" />
                Book Appointment
              </Link>
            </div>

            {/* Meet Bong - Light Grey Background */}
            <div className="bg-slate-100 p-8 lg:p-12 rounded-r-2xl lg:rounded-r-2xl rounded-b-2xl lg:rounded-b-none">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Meet Bong
              </h2>
              <div className="flex flex-col items-start gap-6">
                <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src="/images/Homepage_pic.jpg" 
                    alt="Bongekile - Physiotherapist" 
                    className="w-full h-auto"
                    data-testid="meet-bong-image"
                  />
                </div>
                <div>
                  <p className="text-slate-600 mb-4">
                    Bongekile "Bong" Ngcamphalala is a passionate physiotherapist dedicated to helping patients achieve their recovery goals.
                  </p>
                  <Link 
                    to="/about" 
                    className="inline-flex items-center gap-2 text-[#0e7490] font-medium hover:underline"
                    data-testid="read-more-link"
                  >
                    Read More About Bong
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section - White Background */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-8 md:p-12 shadow-sm border border-slate-100 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#f0fdfd] flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-[#0e7490]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">BSc (Hons) Physiotherapy</h3>
                  <p className="text-sm text-slate-500">University of Limpopo - Medunsa Campus</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#f0fdfd] flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-[#0e7490]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Neurological Rehabilitation (Bobath)</h3>
                  <p className="text-sm text-slate-500">Special Interest & Certification</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#f0fdfd] flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-[#0e7490]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Member of HPCSA</h3>
                  <p className="text-sm text-slate-500">Health Professions Council of South Africa</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#f0fdfd] flex items-center justify-center flex-shrink-0">
                  <Home className="w-6 h-6 text-[#0e7490]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Practice No: 0950750</h3>
                  <p className="text-sm text-slate-500">Registered Practice</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#5ce1e6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Start Your Recovery Journey?
          </h2>
          <p className="text-slate-700 mb-8 text-lg">
            Book an appointment today and take the first step towards better health.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/book" 
              className="inline-flex items-center justify-center px-8 py-3 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-colors"
              data-testid="cta-book-btn"
            >
              Book Appointment
            </Link>
            <Link 
              to="/treatments" 
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-slate-900 rounded-full font-medium hover:bg-slate-50 transition-colors"
              data-testid="cta-treatments-btn"
            >
              View Treatments
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
